import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/PhotoUpload";
import { mbtiInfo, clubInfo } from "@/lib/mbti";
import { Users, Lock, Eye, Target, Compass, ShieldCheck } from "lucide-react";
import GPSViewerModal from "@/components/gps/GPSViewerModal";
import MentorDigestWidget from "@/components/gps/MentorDigestWidget";

/**
 * Page for Coach (or Admin) to see the identity statements of consultants in their club.
 * Privacy: only consultants who opted-in (share_identity_with_coach=True) reveal the text.
 * Others show a "Privado" badge. Identity bonus points always count regardless of sharing.
 */
export default function MyConsultants() {
  const { user } = useAuth();
  const [players, setPlayers] = useState(null);
  const [gpsStatus, setGpsStatus] = useState({});  // player_id -> { shared, has_135, has_411 }
  const [gpsOpen, setGpsOpen] = useState(null); // {id, name}
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get("/coach/players")
      .then((r) => setPlayers(r.data))
      .catch((e) => setErr(formatApiError(e)));
    api.get("/gps/coach/players")
      .then((r) => {
        const m = {};
        (r.data.consultants || []).forEach((c) => { m[c.player_id] = c; });
        setGpsStatus(m);
      })
      .catch(() => {});
  }, []);

  const sorted = useMemo(() => {
    if (!players) return [];
    return [...players].sort((a, b) => (a.name || "").localeCompare(b.name || "", "pt"));
  }, [players]);

  const counts = useMemo(() => {
    if (!players) return { total: 0, shared: 0 };
    const shared = players.filter((p) => p.share_identity_with_coach).length;
    return { total: players.length, shared };
  }, [players]);

  const clubCtx = user?.club ? clubInfo(user.club) : null;

  if (err) return <div className="kw-card p-6 text-[#FF6B6B]" data-testid="my-consultants-error">{err}</div>;
  if (!players) return <div className="kw-overline">A carregar consultores…</div>;

  return (
    <div className="space-y-6 kw-fade-up" data-testid="my-consultants-page">
      <div>
        <div className="kw-overline">Vista do Secretário</div>
        <h1 className="font-display text-4xl md:text-5xl font-black uppercase tracking-tighter">
          Os Meus <span style={{ color: clubCtx?.color || "#E50000" }}>Consultores</span>
        </h1>
        <p className="text-sm text-[#A3A3A3] mt-2 max-w-2xl">
          Vês os consultores do teu clube{user?.club ? ` (${user.club})` : ""}.
          Cada consultor decide se partilha contigo a sua Missão, Visão e Princípios.
        </p>
      </div>

      <MentorDigestWidget />

      <div className="grid sm:grid-cols-3 gap-3">
        <Metric label="Total de consultores" value={counts.total} testid="metric-total" />
        <Metric label="Partilharam contigo" value={counts.shared} color="#22C55E" testid="metric-shared" />
        <Metric label="Privados" value={counts.total - counts.shared} color="#737373" testid="metric-private" />
      </div>

      {sorted.length === 0 ? (
        <div className="kw-card p-6 text-sm text-[#737373] italic">Sem consultores atribuídos ao teu clube.</div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-4">
          {sorted.map((p) => (
            <ConsultantIdentityCard
              key={p.id}
              p={p}
              gps={gpsStatus[p.id]}
              onOpenGps={() => setGpsOpen({ id: p.id, name: p.name })}
            />
          ))}
        </div>
      )}

      {gpsOpen && (
        <GPSViewerModal
          playerId={gpsOpen.id}
          playerName={gpsOpen.name}
          onClose={() => setGpsOpen(null)}
        />
      )}
    </div>
  );
}

function Metric({ label, value, color = "#E50000", testid }) {
  return (
    <div className="kw-card p-4" data-testid={testid}>
      <div className="kw-overline text-[10px]">{label}</div>
      <div className="font-display text-4xl font-black mt-1" style={{ color }}>{value}</div>
    </div>
  );
}

function ConsultantIdentityCard({ p, gps, onOpenGps }) {
  const club = p.club ? clubInfo(p.club) : null;
  const mbti = p.coach_mbti || p.mbti;
  const mbtiCtx = mbti ? mbtiInfo(mbti) : null;
  const shared = p.share_identity_with_coach;
  const hasAny = Boolean(p.mission || p.vision || p.principles);
  const gpsShared = gps?.shared;
  const gpsHasContent = gps && (gps.has_135 || gps.has_411);

  return (
    <div className="kw-card p-5 flex gap-4" data-testid={`consultant-card-${p.id}`} style={{ borderColor: club?.border }}>
      <div className="flex-shrink-0">
        <Avatar photoUrl={p.photo_url} name={p.name} size={56} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div>
            <div className="font-display text-lg font-black uppercase tracking-tight">{p.name}</div>
            {mbti && (
              <div className="inline-block mt-1 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 border font-mono"
                style={{ color: mbtiCtx?.color, borderColor: mbtiCtx?.border, background: mbtiCtx?.bg }}>
                {mbti}{mbtiCtx?.label ? ` · ${mbtiCtx.label}` : ""}
              </div>
            )}
          </div>
          {shared ? (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 border border-[#22C55E] text-[#22C55E]" data-testid={`shared-badge-${p.id}`}>
              <Eye className="w-3 h-3" /> Partilhado
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 border border-[#444] text-[#737373]" data-testid={`private-badge-${p.id}`}>
              <Lock className="w-3 h-3" /> Privado
            </span>
          )}
        </div>

        <div className="mt-4 space-y-3">
          {shared && hasAny ? (
            <>
              {p.mission && <IdentityBlock icon={Target} label="Missão" text={p.mission} color="#E50000" />}
              {p.vision && <IdentityBlock icon={Compass} label="Visão" text={p.vision} color="#3B82F6" />}
              {p.principles && <IdentityBlock icon={ShieldCheck} label="Princípios" text={p.principles} color="#22C55E" />}
            </>
          ) : shared && !hasAny ? (
            <p className="text-xs text-[#737373] italic">
              Este consultor ativou a partilha mas ainda não preencheu nenhum dos campos.
            </p>
          ) : (
            <p className="text-xs text-[#737373] italic">
              Privado por opção do consultor. Os pontos de bónus (+3 por campo preenchido)
              contam sempre na liga.
            </p>
          )}

          {/* GPS access */}
          <div className="pt-3 border-t border-[#222] flex items-center gap-2 flex-wrap">
            <Compass className="w-3.5 h-3.5 text-[#E50000]" />
            <span className="text-[10px] uppercase tracking-widest text-[#A3A3A3] font-bold">GPS 1-3-5 & 4-1-1</span>
            {gps ? (
              gpsShared ? (
                <>
                  <span className="text-[10px] text-[#22C55E]">
                    {gpsHasContent ? "Preenchido" : "Vazio"}
                  </span>
                  <button
                    onClick={onOpenGps}
                    className="ml-auto kw-btn-secondary !py-1 !px-2 text-[10px]"
                    data-testid={`view-gps-${p.id}`}
                  >
                    Ver GPS
                  </button>
                </>
              ) : (
                <span className="ml-auto text-[10px] text-[#737373] italic inline-flex items-center gap-1">
                  <Lock className="w-3 h-3" /> Privado
                </span>
              )
            ) : (
              <span className="ml-auto text-[10px] text-[#737373] italic">Sem dados</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function IdentityBlock({ icon: Icon, label, text, color }) {
  return (
    <div className="border-l-2 pl-3" style={{ borderColor: color }}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className="w-3 h-3" style={{ color }} />
        <span className="text-[10px] uppercase tracking-widest font-bold" style={{ color }}>{label}</span>
      </div>
      <p className="text-xs text-[#E5E5E5] whitespace-pre-line leading-snug">{text}</p>
    </div>
  );
}
